import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import "../../i18n";
import { Notice } from "../../design/components";
import { useReprocess } from "../../api/queries";
import type { DocumentSummary } from "../../api/types";
import styles from "./DocumentsQueue.module.css";

const STALLED_AFTER_MS = 15 * 60_000;
const NOTICE_MS = 4000;

export function isStalled(row: DocumentSummary, now: number = Date.now()): boolean {
  return row.state === "in_flight" && now - new Date(row.updatedAt).getTime() > STALLED_AFTER_MS;
}

export function canReprocess(row: DocumentSummary, now?: number): boolean {
  return row.state === "error" || isStalled(row, now);
}

export function documentLabel(row: DocumentSummary): string {
  return row.erpDocId ?? row.uuid ?? row.id;
}

/** Reprocess is the only M1 action; B11 reuses this component in the Inspector. */
export function RowActions({
  row,
  onOpenChange,
}: {
  row: DocumentSummary;
  onOpenChange?: ((open: boolean) => void) | undefined;
}) {
  const { t } = useTranslation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [notice, setNotice] = useState<{ kind: "ok" | "fail"; text: string } | null>(null);
  const reprocess = useReprocess();
  const label = documentLabel(row);

  useEffect(() => {
    onOpenChange?.(menuOpen || confirming);
  }, [menuOpen, confirming, onOpenChange]);

  useEffect(() => {
    if (notice?.kind !== "ok") return;
    const timer = window.setTimeout(() => setNotice(null), NOTICE_MS);
    return () => window.clearTimeout(timer);
  }, [notice]);

  if (!canReprocess(row)) return null;

  function confirm() {
    reprocess.mutate(row.id, {
      onSuccess: () => {
        setConfirming(false);
        setNotice({
          kind: "ok",
          text: t("documents.rowActions.reprocessRequested", "Reprocess requested for {{label}}", { label }),
        });
      },
      onError: (error: Error) => {
        setConfirming(false);
        setNotice({ kind: "fail", text: error.message });
      },
    });
  }

  return (
    <>
      <button
        type="button"
        className="btn btn-sm btn-ghost"
        aria-haspopup="menu"
        aria-expanded={menuOpen}
        aria-label={t("documents.rowActions.actionsFor", "Actions for {{label}}", { label })}
        onClick={() => setMenuOpen((open) => !open)}
      >
        {t("documents.rowActions.actions", "Actions")}
      </button>

      {menuOpen ? (
        <div className={styles.menu} role="menu">
          <button
            type="button"
            role="menuitem"
            className={styles.menuItem}
            onClick={() => {
              setMenuOpen(false);
              setConfirming(true);
            }}
          >
            {t("documents.rowActions.reprocess", "Reprocess")}
          </button>
        </div>
      ) : null}

      {confirming ? (
        <div className={styles.dialogWrap}>
          <div
            className={styles.dialog}
            role="dialog"
            aria-modal="true"
            aria-label={t("documents.rowActions.reprocessDialogLabel", "Reprocess {{label}}", { label })}
          >
            <h2 className={styles.dialogTitle}>
              {t("documents.rowActions.reprocessConfirmTitle", "Reprocess {{label}}?", { label })}
            </h2>
            <p className={styles.dialogBody}>
              {t(
                "documents.rowActions.reprocessConfirmBody",
                "The document re-enters the pipeline at its current stage. Nothing already sent is withdrawn.",
              )}
            </p>
            <div className={styles.dialogActions}>
              <button
                type="button"
                className="btn btn-sm btn-ghost"
                onClick={() => setConfirming(false)}
                disabled={reprocess.isPending}
              >
                {t("documents.rowActions.cancel", "Cancel")}
              </button>
              <button
                type="button"
                className="btn btn-sm"
                onClick={confirm}
                disabled={reprocess.isPending}
                aria-label={t("documents.rowActions.reprocessDocument", "Reprocess document")}
              >
                {reprocess.isPending
                  ? t("documents.rowActions.reprocessing", "Reprocessing…")
                  : t("documents.rowActions.reprocessDocument", "Reprocess document")}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {notice ? (
        <div className={styles.rowNotice}>
          <Notice kind={notice.kind}>{notice.text}</Notice>
        </div>
      ) : null}
    </>
  );
}
